import { Compass, BookOpen, Hammer, Heart, Crown, Check, Sparkles } from 'lucide-react'

const journeySteps = [
  {
    icon: Compass,
    label: 'Descubra',
    xp: '+50 XP',
    color: 'bg-brand-coral text-brand-dark',
    state: 'done' as const
  },
  {
    icon: BookOpen,
    label: 'Aprenda',
    xp: '+120 XP',
    color: 'bg-brand-green text-brand-dark',
    state: 'done' as const
  },
  {
    icon: Hammer,
    label: 'Pratique',
    xp: '+200 XP',
    color: 'bg-brand-cyan text-brand-dark',
    state: 'current' as const
  },
  {
    icon: Heart,
    label: 'Impacte',
    xp: '+300 XP',
    color: 'bg-brand-pink text-white',
    state: 'locked' as const
  },
  {
    icon: Crown,
    label: 'Missão Futuro',
    xp: '+500 XP',
    color: 'bg-brand-purple text-white',
    state: 'locked' as const
  }
]

export function LandingJourneySection() {
  return (
    <section id="jornadas" className="relative overflow-hidden bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeader
          eyebrow="Sua Jornada"
          title={
            <>
              Cinco fases.{' '}
              <span className="font-hl text-brand-purple">Uma transformação.</span>
            </>
          }
          subtitle="Avance por um mapa gamificado de competências empreendedoras. Cada fase é um novo nível desbloqueado."
        />

        <div className="relative mt-16">
          {/* Conector dashed — desktop */}
          <svg
            className="hidden md:block absolute left-0 right-0 top-1/2 -translate-y-1/2 w-full h-32"
            viewBox="0 0 1200 120"
            fill="none"
            preserveAspectRatio="none"
            aria-hidden
          >
            <path
              d="M40 60 C 180 -10, 320 130, 480 60 S 760 -10, 920 60 S 1160 130, 1200 60"
              stroke="var(--color-brand-blue)"
              strokeWidth="4"
              strokeDasharray="2 14"
              strokeLinecap="round"
              opacity="0.35"
            />
          </svg>

          <ol className="relative grid gap-8 md:grid-cols-5 md:gap-4">
            {journeySteps.map((s, i) => {
              const Icon = s.icon
              const isCurrent = s.state === 'current'
              const isDone = s.state === 'done'

              return (
                <li
                  key={s.label}
                  className={`relative flex md:flex-col items-center gap-4 md:gap-3 md:text-center ${
                    i % 2 === 1 ? 'md:translate-y-10' : ''
                  }`}
                >
                  <div
                    className={`relative grid size-24 md:size-28 place-items-center rounded-3xl ${s.color} shadow-[0_8px_0_var(--color-brand-dark)] ${
                      isCurrent ? 'ring-4 ring-primary animate-pulse-soft' : ''
                    } ${s.state === 'locked' ? 'opacity-50 grayscale' : ''}`}
                  >
                    <Icon className="size-10" />
                    {isDone && (
                      <div className="absolute -right-2 -top-2 grid size-8 place-items-center rounded-full bg-brand-green text-brand-dark ring-4 ring-white">
                        <Check className="size-4" strokeWidth={3} />
                      </div>
                    )}
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-dark px-3 py-1 font-hl text-xs text-brand-yellow whitespace-nowrap">
                      {s.xp}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">
                      Fase 0{i + 1}
                    </div>
                    <div className="font-hl text-2xl text-gray-900">{s.label}</div>
                    {isCurrent && (
                      <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand-blue">
                        <Sparkles className="size-3" /> Você está aqui
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      </div>
    </section>
  )
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  dark
}: {
  eyebrow: string
  title: React.ReactNode
  subtitle?: string
  dark?: boolean
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <div
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest ${
          dark ? 'bg-white/10 text-brand-coral' : 'bg-brand-blue/10 text-brand-blue'
        }`}
      >
        {eyebrow}
      </div>
      <h2
        className={`mt-5 font-display text-4xl font-black leading-[1.05] md:text-5xl lg:text-6xl ${
          dark ? 'text-white' : 'text-gray-900'
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`mx-auto mt-4 max-w-2xl text-lg ${
            dark ? 'text-white/80' : 'text-gray-500'
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  )
}

export { SectionHeader }
